import { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Text from 'components/core/Text';
import { updateProgramSchedule } from 'actions/programs';
import { scheduleObject, scheduleOrder, scheduleTemplate } from 'constants/schedule';
import { ProgramScheduleType, Program } from 'reducers/programs/types';
import ButtonAddToCalendar from 'components/ButtonAddToCalendar';

import { Wrapper, TitleRow, Title, Week, Day, CalendarRow, ProgramLink } from './styles';

type Weekday = keyof ProgramScheduleType;

interface Props {
  program?: Program;

  /**
   * Show a link to the program above the schedule
   * @default false
   */
  showProgram?: boolean;
}

const ProgramSchedule: FC<Props> = ({ program, showProgram }) => {
  const dispatch = useDispatch();
  const schedule = program?.schedule;
  const scheduledDays = useMemo(
    () => (scheduleOrder as Weekday[]).filter((day) => !!schedule?.[day]),
    [schedule],
  );

  const handleDayClick = useCallback(
    (day: Weekday) => {
      if (!program) return;
      dispatch(
        updateProgramSchedule({
          programId: program.id,
          schedule: { ...scheduleObject, ...schedule, [day]: !schedule?.[day] },
        }),
      );
    },
    [dispatch, program, schedule],
  );

  if (!program) {
    return null;
  }

  return (
    <Wrapper>
      {showProgram && <ProgramLink href={`/programs/${program.id}`}>{program.name}</ProgramLink>}
      <TitleRow>
        <Title>Schedule</Title>
        <Text variant="textSmall" color="gray">
          {scheduledDays.length > 0
            ? `${scheduledDays.length} ${scheduledDays.length === 1 ? 'time' : 'times'} per week`
            : 'No schedule set'}
        </Text>
      </TitleRow>
      <Week>
        {(scheduleOrder as Weekday[]).map((day) => (
          <Day
            key={day}
            type="button"
            active={!!schedule?.[day]}
            aria-pressed={!!schedule?.[day]}
            aria-label={scheduleTemplate[day].name}
            onClick={() => handleDayClick(day)}
          >
            {day[0]}
          </Day>
        ))}
      </Week>
      {scheduledDays.length > 0 && (
        <CalendarRow>
          <ButtonAddToCalendar asLink program={program} schedule={scheduledDays} />
        </CalendarRow>
      )}
    </Wrapper>
  );
};

export default ProgramSchedule;
